import React, { useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { ParticleEffectType, ParticleShape } from '../utils/particleEffectsUtils';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
  size: number;
  rotation: number;
  rotationSpeed: number;
  shape: ParticleShape;
  opacity: number;
  emoji?: string;
  type?: string;
}

interface ParticleEffectsProps {
  trigger: {
    type: ParticleEffectType | 'yatzy' | 'record' | 'achievement' | 'glow' | null;
    x?: number;
    y?: number;
    intensity?: number;
  };
  onComplete?: () => void;
}

const Canvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 9999;
`;

const ParticleEffects: React.FC<ParticleEffectsProps> = ({ trigger, onComplete }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
    const animationRef = useRef<number | undefined>(undefined);
  const particlesRef = useRef<Particle[]>([]);

  const startEffect = useCallback(() => {
    if (!trigger.type) return;
  const canvas = canvasRef.current;
  if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    createParticles(trigger.type, trigger.x, trigger.y);
    animate(ctx);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger.type, trigger.x, trigger.y]);

  useEffect(() => {
    startEffect();
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, [startEffect]);

  const createParticles = (effectType: string, x?: number, y?: number) => {
    const particles: Particle[] = [];
    const centerX = x || window.innerWidth / 2;
    const centerY = y || window.innerHeight / 2;

    switch (effectType) {
      case 'yatzy':
        // Explosiv fyrverkeri för Yatzy!
        for (let i = 0; i < 100; i++) {
          const angle = (Math.PI * 2 * i) / 100;
          const velocity = 3 + Math.random() * 5;
          particles.push({
            x: centerX,
            y: centerY,
            vx: Math.cos(angle) * velocity,
            vy: Math.sin(angle) * velocity,
            life: 120,
            maxLife: 120,
            color: `hsl(${Math.random() * 360}, 100%, 60%)`,
            size: 3 + Math.random() * 4,
            type: 'yatzy',
            rotation: Math.random() * Math.PI * 2,
            rotationSpeed: (Math.random() - 0.5) * 0.2,
            shape: ParticleShape.STAR,
            opacity: 1
          });
        }
        break;

      case 'record':
        // Konfetti från toppen
        for (let i = 0; i < 80; i++) {
          particles.push({
            x: Math.random() * window.innerWidth,
            y: -10,
            vx: (Math.random() - 0.5) * 4,
            vy: 2 + Math.random() * 3,
            life: 180,
            maxLife: 180,
            color: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'][Math.floor(Math.random() * 6)],
            size: 4 + Math.random() * 6,
            type: 'confetti',
            rotation: Math.random() * Math.PI * 2,
            rotationSpeed: (Math.random() - 0.5) * 0.2,
            shape: ParticleShape.SQUARE,
            opacity: 1
          });
        }
        break;

      case 'achievement':
        // Stjärnregn
        for (let i = 0; i < 50; i++) {
          const angle = (Math.PI * 2 * i) / 50;
          const velocity = 2 + Math.random() * 3;
          particles.push({
            x: centerX,
            y: centerY,
            vx: Math.cos(angle) * velocity,
            vy: Math.sin(angle) * velocity,
            life: 90,
            maxLife: 90,
            color: '#FFD700',
            size: 2 + Math.random() * 3,
            type: 'sparkle',
            rotation: Math.random() * Math.PI * 2,
            rotationSpeed: (Math.random() - 0.5) * 0.1,
            shape: ParticleShape.STAR,
            opacity: 1
          });
        }
        break;

      case 'glow':
        // Subtila glimtar
        for (let i = 0; i < 20; i++) {
          particles.push({
            x: centerX + (Math.random() - 0.5) * 100,
            y: centerY + (Math.random() - 0.5) * 100,
            vx: (Math.random() - 0.5) * 2,
            vy: (Math.random() - 0.5) * 2,
            life: 60,
            maxLife: 60,
            color: '#FFD700',
            size: 1 + Math.random() * 2,
            type: 'sparkle',
            rotation: 0,
            rotationSpeed: 0,
            shape: ParticleShape.CIRCLE,
            opacity: 0.8
          });
        }
        break;
    }

    particlesRef.current = particles;
  };

  const animate = (ctx: CanvasRenderingContext2D) => {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    const particles = particlesRef.current;
    
    for (let i = particles.length - 1; i >= 0; i--) {
      const particle = particles[i];
      
      // Uppdatera position
      particle.x += particle.vx;
      particle.y += particle.vy;
      
      // Uppdatera hastighet (gravitation för konfetti)
      if (particle.type === 'confetti') {
        particle.vy += 0.1;
        particle.vx *= 0.99;
      } else if (particle.type === 'yatzy' || particle.type === 'firework') {
        particle.vx *= 0.98;
        particle.vy *= 0.98;
      }
      
      // Minska livstid
      particle.life--;
      
      // Ta bort döda partiklar
      if (particle.life <= 0) {
        particles.splice(i, 1);
        continue;
      }
      
      // Rita partikel
      const alpha = particle.life / particle.maxLife;
      ctx.save();
      
      if (particle.type === 'confetti') {
        // Rektangulär konfetti med rotation
        ctx.globalAlpha = alpha;
        ctx.fillStyle = particle.color;
        ctx.translate(particle.x, particle.y);
        ctx.rotate((particle.life * 0.1) % (Math.PI * 2));
        ctx.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size * 1.5);
      } else if (particle.type === 'sparkle') {
        // Stjärnformade sparkles
        ctx.globalAlpha = alpha;
        ctx.fillStyle = particle.color;
        ctx.shadowColor = particle.color;
        ctx.shadowBlur = 10;
        drawStar(ctx, particle.x, particle.y, particle.size);
      } else {
        // Cirkulära partiklar
        ctx.globalAlpha = alpha;
        ctx.fillStyle = particle.color;
        ctx.shadowColor = particle.color;
        ctx.shadowBlur = 5;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();
      }
      
      ctx.restore();
    }

    if (particles.length > 0) {
      animationRef.current = requestAnimationFrame(() => animate(ctx));
    } else if (onComplete) {
      onComplete();
    }
  };

  const drawStar = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number) => {
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      const angle = (i * Math.PI * 2) / 5 - Math.PI / 2;
      const x1 = x + Math.cos(angle) * size;
      const y1 = y + Math.sin(angle) * size;
      
      const angle2 = ((i + 0.5) * Math.PI * 2) / 5 - Math.PI / 2;
      const x2 = x + Math.cos(angle2) * (size * 0.5);
      const y2 = y + Math.sin(angle2) * (size * 0.5);
      
      if (i === 0) {
        ctx.moveTo(x1, y1);
      } else {
        ctx.lineTo(x1, y1);
      }
      ctx.lineTo(x2, y2);
    }
    ctx.closePath();
    ctx.fill();
  };

  if (!trigger.type) return null;

  return <Canvas ref={canvasRef} />;
};

export default ParticleEffects;
